import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import { NotificationsService } from 'angular2-notifications';
import { FeesService } from '../../../../fees.service';
import { ProgramService } from '../../../../services/program.service';
import { AppFields, ClubProgram, Store, AppState, Observable, ProgramsBillingType, map, ProgramsBillingName, StorageUtil } from '../../../../store';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-tryout-billing-info',
  templateUrl: './tryout-billing-info.component.html',
  styleUrls: ['./tryout-billing-info.component.css']
})
export class TryoutBillingInfoComponent implements OnInit {
  public billingInfoForm: FormGroup;
  public programId: Number;
  public routeParams;
  public isStep2WizardNextButtonClicked: number;
  public is_active_step2_wizard: boolean;
  public is_enable_accept_credit_cards: boolean;
  public is_enable_accept_mail_in_payments_controls: boolean;
  public zipCodePattern: RegExp;
  public alphaNumericPattern: RegExp;
  public flagAreYouCollectingFees: number;
  public validationMessages = {};
  public states: any[];
  public isStripIdAvailable: boolean;
  public isRecordAvailable: boolean;
  public isShowAreYouCollectingFeeNoCaption: boolean;
  public isShowSubmitButton: boolean;

  program: Observable<ClubProgram>;
  team: any;
  club: any;
  clubProgramTypeID: number;

  constructor(private activeRoute: ActivatedRoute,
    private router: Router,
    private feesService: FeesService,
    private programService: ProgramService,
    private notificationsService: NotificationsService,
    private spinnerService: Ng4LoadingSpinnerService,
    private fb: FormBuilder,
    private store: Store<AppState>,
  ) {
    this.isShowSubmitButton = false;
    this.isShowAreYouCollectingFeeNoCaption = false;
    this.isRecordAvailable = false;
    this.isStep2WizardNextButtonClicked = 0;
    this.is_active_step2_wizard = false;
    this.is_enable_accept_credit_cards = false;
    this.is_enable_accept_mail_in_payments_controls = false;
    this.flagAreYouCollectingFees = 1;
    this.zipCodePattern = /^[0-9 \-]+$/;
    this.alphaNumericPattern = /^[a-zA-Z0-9_ ]+$/;
    this.validationMessages = {
      'AreYouCollectingFeesRequired': 'Please select an option.',
      'AcceptCreditCardsRequired': 'Please select an option.',
      'AddressRequired': 'This field is required.',
      'AddressMaxlength': 'Please enter no more than 250 number.',
      'AddressPattern': 'Only alphanumeric characters and spaces are valid in this field.',
      'CityRequired': 'This field is required.',
      'CityMaxlength': 'Please enter no more than 150 number.',
      'CityPattern': 'Only alphanumeric characters and spaces are valid in this field.',
      'PostalCodeRequired': 'This field is required.',
      'PostalCodeMinMaxlength': 'Please enter at least 5 characters and no more than 10 characters.',
      'PostalCodePattern': 'Please enter only number and hyphen.',
      'errorStateRequired': 'This field is required.',
    };

    this.program = this.store.select(AppFields.App, AppFields.Program);
  }

  ngOnInit() {
    this.routeParams = this.activeRoute.snapshot.params;
    this.programId = this.routeParams.id;
    this.getStates();
    this.initForm();
    this.setClubTeamAndProgramData();
  }

  setClubTeamAndProgramData() {
    this.program.pipe(filter(p => !!p), take(1)).subscribe(
      (p: ClubProgram) => {
        this.programId = p.clubprogramID;

        if (typeof this.routeParams.feeId !== 'undefined') {
          this.getFeeData(this.routeParams.feeId);
        }
        this.checkIsStripeAccountSetup();

        this.clubProgramTypeID = p.clubProgramTypeID;

        this.store.select(AppFields.App, AppFields.Club).pipe(take(1)).subscribe(c => {
          this.club = c;
        });
      }
    );
  }

  initForm() {
    this.billingInfoForm = this.fb.group({
      AreYouCollectingFees: ['', [Validators.required]],
      AcceptCreditCard: ['', [Validators.required]],
      AcceptMailInPayment: ['', [Validators.required]],
      MailInAddress: ['', Validators.compose([Validators.required, Validators.maxLength(250),
      Validators.pattern(this.alphaNumericPattern)])],
      MailInCity: ['', Validators.compose([Validators.required, Validators.maxLength(150),
      Validators.pattern(this.alphaNumericPattern)])],
      MailInState: ['', Validators.required],
      MailInZip: ['', Validators.compose([Validators.required, Validators.minLength(5),
      Validators.maxLength(10), Validators.pattern(this.zipCodePattern)])]
    }, {
      validator: [
        this.mailInZipStartsWithDigitValidation('MailInZip'),
        this.mailInZipEndsWithDigitValidation('MailInZip')
      ]
    });
  }

  getStates() {
    this.feesService.getStates().subscribe((response: any) => {
      this.states = response;
    });
  }

  getFeeData(fee_id: number) {
    this.spinnerService.show();
    this.feesService.getFees(fee_id.toString()).subscribe((response: any) => {
      const da = response.data;
      if (da.setup !== 1) {
        this.billingInfoForm.patchValue({
          AreYouCollectingFees: (da.collectingfees !== null) ? da.collectingfees.toString() : null,
          AcceptCreditCard: da.acceptcreditcard.toString(),
          AcceptMailInPayment: da.acceptmailinpayment.toString(),
          MailInAddress: da.mailinAddress,
          MailInCity: da.mailincity,
          MailInState: da.mailinstateID,
          MailInZip: da.mailinzip,
        });
        this.areYouCollectingFeesSelection();
        this.acceptCreditCardSelection();
        this.acceptMailInPaymentSelection();
        this.isRecordAvailable = true;
      }
      this.spinnerService.hide();
    });
  }

  mailInZipStartsWithDigitValidation(mailInZip: string) {
    return (group: FormGroup): { [key: string]: any } => {
      const zipcode = group.controls[mailInZip];
      const reg = /^\d+$/;
      if (zipcode.value != null) {
        if (!reg.test(zipcode.value.charAt(0))) {
          return {
            mailInZipStartsWithDigitError: 'Please start postal code with number.'
          };
        }
      }
    };
  }

  mailInZipEndsWithDigitValidation(mailInZip: string) {
    return (group: FormGroup): { [key: string]: any } => {
      const zipcode = group.controls[mailInZip];
      const reg = /^\d+$/;
      if (zipcode.value != null) {
        if (!reg.test(zipcode.value.charAt(zipcode.value.length - 1))) {
          return {
            mailInZipEndsWithDigitError: 'Please end postal code with number.'
          };
        }
      }
    };
  }

  validateBillingInformation() {
    this.billingInfoForm.get('MailInAddress').setValidators(Validators.compose([
      Validators.required, Validators.maxLength(250), Validators.pattern(this.alphaNumericPattern)
    ]));
    this.billingInfoForm.get('MailInCity').setValidators(Validators.compose([
      Validators.required, Validators.maxLength(150), Validators.pattern(this.alphaNumericPattern)
    ]));
    this.billingInfoForm.get('MailInState').setValidators([Validators.required]);
    this.billingInfoForm.get('MailInZip').setValidators(Validators.compose([
      Validators.required, Validators.minLength(5), Validators.maxLength(10), Validators.pattern(this.zipCodePattern)
    ]));
    this.billingInfoForm.get('MailInAddress').updateValueAndValidity();
    this.billingInfoForm.get('MailInCity').updateValueAndValidity();
    this.billingInfoForm.get('MailInState').updateValueAndValidity();
    this.billingInfoForm.get('MailInZip').updateValueAndValidity();
  }

  removeValidaion(name) {
    const control = this.billingInfoForm.get(name);
    if (!this.isRecordAvailable) {
      control.reset();
    }
    control.clearValidators();
    control.updateValueAndValidity();
  }

  isAreYouCollectingFeesValid() {
    return this.billingInfoForm.get('AreYouCollectingFees').errors
      && (this.billingInfoForm.get('AreYouCollectingFees').touched
        || this.billingInfoForm.get('AreYouCollectingFees').dirty);
  }

  isAcceptCreditCardValid() {
    return this.billingInfoForm.get('AcceptCreditCard').errors
      && (this.billingInfoForm.get('AcceptCreditCard').touched
        || this.billingInfoForm.get('AcceptCreditCard').dirty);
  }

  isAcceptMailInPaymentValid() {
    return this.billingInfoForm.get('AcceptMailInPayment').errors
      && (this.billingInfoForm.get('AcceptMailInPayment').touched
        || this.billingInfoForm.get('AcceptMailInPayment').dirty);
  }

  isMailInAddressValid() {
    return this.billingInfoForm.get('MailInAddress').errors
      && (this.billingInfoForm.get('MailInAddress').touched
        || this.billingInfoForm.get('MailInAddress').dirty);
  }

  isMailInCityValid() {
    return this.billingInfoForm.get('MailInCity').errors
      && (this.billingInfoForm.get('MailInCity').touched
        || this.billingInfoForm.get('MailInCity').dirty);
  }

  isMailInStateValid() {
    return this.billingInfoForm.get('MailInState').errors
      && (this.billingInfoForm.get('MailInState').touched
        || this.billingInfoForm.get('MailInState').dirty);
  }

  isMailInZipValid() {
    return this.billingInfoForm.get('MailInZip').errors
      && (this.billingInfoForm.get('MailInZip').touched
        || this.billingInfoForm.get('MailInZip').dirty);
  }

  //isAreYouCollectingFeesYesSelectedInit() {
  //  if (this.flagAreYouCollectingFees === 1) {
  //    return true;
  //  } else {
  //    return (!!this.billingInfoForm.get('AreYouCollectingFees'))
  //      && (this.billingInfoForm.get('AreYouCollectingFees').value === '1');
  //  }
  //}

  isAreYouCollectingFeesYesSelected() {
    return (!!this.billingInfoForm.get('AreYouCollectingFees'))
      && (this.billingInfoForm.get('AreYouCollectingFees').value === '1');
  }

  isAcceptCreditCardsYesSelected() {
    return (!!this.billingInfoForm.get('AcceptCreditCard'))
      && (this.billingInfoForm.get('AcceptCreditCard').value === '1');
  }

  isAcceptMailInPaymentsYesSelected() {
    return (!!this.billingInfoForm.get('AcceptMailInPayment'))
      && (this.billingInfoForm.get('AcceptMailInPayment').value === '1');
  }

  areYouCollectingFeesSelection() {
    if (this.isAreYouCollectingFeesYesSelected()) {
      this.isShowSubmitButton = false;
      this.is_enable_accept_mail_in_payments_controls = false;
      this.isShowAreYouCollectingFeeNoCaption = false;
      this.billingInfoForm.get('AcceptCreditCard').setValidators([Validators.required]);
      this.billingInfoForm.get('AcceptMailInPayment').setValidators([Validators.required]);
      this.billingInfoForm.get('AcceptCreditCard').updateValueAndValidity();
      this.billingInfoForm.get('AcceptMailInPayment').updateValueAndValidity();
      this.validateBillingInformation();
    } else {
      this.isShowSubmitButton = true;
      this.is_enable_accept_mail_in_payments_controls = true;
      this.isShowAreYouCollectingFeeNoCaption = true;
      this.removeValidaion('AcceptCreditCard');
      this.removeValidaion('AcceptMailInPayment');
      this.removeValidaion('MailInAddress');
      this.removeValidaion('MailInCity');
      this.removeValidaion('MailInState');
      this.removeValidaion('MailInZip');
      this.billingInfoForm.patchValue({ 'MailInState': '' });
    }
  }

  areYouCollectingFeesChange(event) {
    event.stopPropagation();
    this.areYouCollectingFeesSelection();
  }

  acceptCreditCardSelection() {
    if (this.isAcceptCreditCardsYesSelected()) {
      this.is_enable_accept_credit_cards = true;
    } else {
      this.is_enable_accept_credit_cards = false;
    }
  }

  acceptCreditCardChange(event) {
    event.stopPropagation();
    this.acceptCreditCardSelection();
  }

  acceptMailInPaymentSelection() {
    if (this.isAcceptMailInPaymentsYesSelected()) {
      this.is_enable_accept_mail_in_payments_controls = true;
      this.validateBillingInformation();
    } else {
      this.is_enable_accept_mail_in_payments_controls = false;
      this.removeValidaion('MailInAddress');
      this.removeValidaion('MailInCity');
      this.removeValidaion('MailInState');
      this.removeValidaion('MailInZip');
    }
  }

  acceptMailInPaymentChange(event) {
    event.stopPropagation();
    this.acceptMailInPaymentSelection();
  }

  validateUi() {
    Object.keys(this.billingInfoForm.controls).forEach(field => {
      const control = this.billingInfoForm.get(field);
      control.markAsTouched({ onlySelf: true });
    });
  }

  previousWizardStep(event, formStatus) {
    event.stopPropagation();
    this.isStep2WizardNextButtonClicked = 1;
    const RP = this.routeParams;
    this.router.navigate(['/tryoutprogram/' + RP.name + '/' + RP.id + '/' + RP.status + '/fee/step1/' + RP.feeId]);
    //this.validateUi();
    //if (formStatus === 'VALID') {
    //  this.saveBillingInfo(event, 1);
    //}
  }

  nextWizardStep(event, formStatus) {
    event.stopPropagation();
    this.isStep2WizardNextButtonClicked = 1;
    this.validateUi();
    if (formStatus === 'VALID') {
      this.saveBillingInfo(event, 2);
    }
  }

  submitWizardStepBillingInfo(event, form_status) {
    event.stopPropagation();
    this.isStep2WizardNextButtonClicked = 1;
    this.validateUi();
    if (form_status === 'VALID') {
      this.saveBillingInfo(event, 0);
    }
  }

  saveBillingInfo(event, buttonFlag) {
    const fee_id = this.routeParams.feeId;
    const obj = {
      FeeID: (typeof fee_id === 'undefined') ? 0 : fee_id.toString(),
      clubprogramID: Number(this.programId),
      Addedby: StorageUtil.userID,
      Collectingfees: Number(this.billingInfoForm.value.AreYouCollectingFees),
      Acceptcreditcard: Number(this.billingInfoForm.value.AcceptCreditCard),
      Acceptmailinpayment: Number(this.billingInfoForm.value.AcceptMailInPayment),
      CheckPayableTo: '',
      MailinAddress: this.billingInfoForm.value.MailInAddress,
      Mailincity: this.billingInfoForm.value.MailInCity,
      MailinstateID: Number(this.billingInfoForm.value.MailInState),
      Mailinzip: this.billingInfoForm.value.MailInZip,
      Setup: 2
    };
    event.target.innerHTML = 'Saving <i class="fa fa-spinner fa-spin push-5-r"></i>';
    event.target.disabled = true;
    this.feesService.saveBillingInfo(obj).subscribe((data: any) => {
      event.target.innerHTML = 'Next <i class="fa fa-arrow-right"></i>';
      event.target.disabled = false;
      this.alertMessage('success', data.result);
      const RP = this.routeParams;
      if (buttonFlag === 1) {
        this.router.navigate(['/tryoutprogram/' + RP.name + '/' + RP.id + '/' + RP.status + '/fee/step1/' + RP.feeId]);
      } else if (buttonFlag === 2) {
        this.router.navigate(['/tryoutprogram/' + RP.name + '/' + RP.id + '/' + RP.status + '/fee/step3/' + RP.feeId]);
      } else {
        this.router.navigate(['/tryoutprogram/' + RP.name + '/' + RP.id + '/' + RP.status + '/fee']);
      }
    }, error => {
      console.error(error);
      this.alertMessage('error', error.statusText);
    });
  }

  alertMessage(status, message) {
    if (status === 'success') {
      const toast = this.notificationsService.success('Success', message, {
        timeOut: 3000,
        showProgressBar: true,
        pauseOnHover: false,
        clickToClose: false
      });
    } else if (status === 'error') {
      const toast = this.notificationsService.error('Error', message, {
        timeOut: 3000,
        showProgressBar: true,
        pauseOnHover: false,
        clickToClose: false
      });
    }
  }

  checkIsStripeAccountSetup() {
    this.programService.getStripeAccountId(+this.programId).subscribe((response: any) => {
      const stripe_id = response.data;
      if ((!!stripe_id) && (stripe_id !== '')) {
        this.isStripIdAvailable = true;
      } else {
        this.isStripIdAvailable = false;
      }
    });
  }

  get clubID() {
    return +localStorage.getItem('clubId');
  }

  get isTeamBilling(): Observable<boolean> {
    return this.program.pipe(map(p => p && (p.clubProgramTypeID === ProgramsBillingType.TeamBilling
      || p.clubProgramTypeID === ProgramsBillingType.CampsClinics)));
  }

  get redirectLink() {
    return window.location.origin + '/stripe/save-user-id';
  }

  get stripeUrl(): string {
    if (this.club) {
      let url = 'https://connect.stripe.com/express/oauth/authorize?stripe_user[business_type]=' + (this.isTeamBilling ? 'individual' : 'company')
        + '&response_type=code&client_id=ca_D5sPxXbDK7G41brzKvh1BxSNuiMSp0ML&state=CA&scope=read_write'
        + '&metadata[club_id]=' + this.club.clubID
        + '&metadata[club]=' + this.club.name
        + '&metadata[club_programtype_id]=' + this.clubProgramTypeID;

      StorageUtil.sClubID = this.clubID;
      StorageUtil.sClubProgramTypeID = this.clubProgramTypeID;
      url += '&redirect_uri=' + this.redirectLink;

      return url;
    } else {
      return '';
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/tryout/pages/tryout-fee/tryout-billing-info/tryout-billing-info.component.ts